import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { FormToggle } from "@/components/form/FormToggle";
import { trackEvent } from "@/utils/trackEvent";

import type { GraphNodeAttrs } from "@/components/tiptap/Graph";
import type { DocumentModel } from "@/models/DocumentModel";

type Props = {
  doc: DocumentModel;
  graphAttrs: GraphNodeAttrs;
  axisVariables: { x: string; y: string };
};

export const GraphAxisLabelsToggler = observer(function GraphAxisLabelsToggler({
  doc,
  graphAttrs,
  axisVariables,
}: Props) {
  const { tiptap } = doc.editor;
  const t = useTranslations("sidebar.activeNode");

  if (!tiptap) return null;

  const handleOnToggleChange = () => {
    const axisLabels = !graphAttrs.axisLabels;
    if (axisLabels) {
      tiptap.commands.updateAttributes("graph", {
        axisLabels: true,
        xAxisLabel: graphAttrs.xAxisLabel || axisVariables.x,
        yAxisLabel: graphAttrs.yAxisLabel || axisVariables.y,
      });
    } else {
      tiptap.commands.updateAttributes("graph", {
        axisLabels: false,
        xAxisLabel: undefined,
        yAxisLabel: undefined,
      });
    }
    trackEvent("graph_axis_labels_toggled", { enabled: !graphAttrs.axisLabels });
  };

  return (
    <label
      htmlFor="graph-axis-labels"
      aria-label={t("graph.actions.axisLabels")}
      className="flex items-center cursor-pointer text-xxs tracking-tight p-1.25 pb-0 uppercase"
    >
      {t("graph.actions.axisLabels")}
      <FormToggle
        id="graph-axis-labels"
        checked={!!graphAttrs.axisLabels}
        className="ml-auto"
        onClick={(e) => e.stopPropagation()}
        onChange={handleOnToggleChange}
        asInput
      />
    </label>
  );
});
